import { Component, computed, effect, inject, input, output } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { ICampaignCreatorForm, ICtaForm } from '@campaign-form/campaign-form.interface';
import { CampaignDatesService } from '@campaign-form/form-fields/campaign-basics/campaign-dates/campaign-dates.service';
import { CtaDialogStore } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.store';
import { DeliverableConfigurationStore } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-configuration.store';
import { CustomValuesService } from '@core/services/custom-values.service';
import { IDeliverableSelectedEvent } from '@cta-analytics-details/cta-analytics-details.interface';
import { EMatchingType, EProcessStatus } from '@enums/campaign-analytics.enum';
import { ECampaignStatus, EDeliverableType } from '@enums/campaign.enum';
import { IAnalyticsNode, ICtaResult } from '@interfaces/campaign-analytics.interface';
import { ICampaignExportApi } from '@pages/campaigns/campaign-details/campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { ICreator } from '@pages/creators/creators.interface';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { SqlToAnalyticsUtilitiesService } from '@utilities/sql-to-analytics-utilities.service';
import { v4 as uuidv4 } from 'uuid';
import { CtaAnalyticsTableComponent } from './cta-analytics-table/cta-analytics-table.component';
import { ICtaAnalyticsData } from './cta-analytics-table/cta-analytics-table.interface';

@Component({
  imports: [CtaAnalyticsTableComponent],
  providers: [DeliverableConfigurationStore, CtaDialogStore],
  selector: 'app-cta-analytics-dialog-content',
  templateUrl: './cta-analytics-dialog-content.component.html',
})
export class CtaAnalyticsDialogContentComponent {
  public readonly ctaResult = input.required<ICtaResult>();
  public readonly creator = input.required<ICreator>();
  public readonly analyticsNode = input.required<IAnalyticsNode>();

  public readonly campaign = input<TCampaignApi | null>(null);
  public readonly briefingId = input<string | null>(null);
  public readonly campaignProcessStatus = input<EProcessStatus | undefined>();
  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly exportMode = input<boolean>(false);
  public readonly campaignExport = input<ICampaignExportApi | null>(null);
  public readonly adminMode = input<boolean>(false);

  public readonly selectedDeliverableId = input<string | null>(null);
  public readonly selectedDeliverableType = input<EDeliverableType | null>(null);
  public readonly selectedDeliverableExecutionAnalytics = input<ICtaAnalyticsData | null>(null);

  public readonly ctaExecutionChange = output<string>();
  public readonly deliverableSelected = output<IDeliverableSelectedEvent>();
  public readonly overviewRequested = output<void>();

  public readonly isNonExecutedExecution = computed(
    () =>
      this.analyticsNode().executionStatus === EMatchingType.planned ||
      this.analyticsNode().executionStatus === EMatchingType.missing,
  );
  public readonly isCampaignInProgress = computed(
    () => this.campaign()?.status !== ECampaignStatus.draft && this.campaign()?.status !== ECampaignStatus.planned,
  );
  public readonly campaignCreatorsFormArray = new FormArray<FormGroup<ICampaignCreatorForm>>([]);

  public ctaDetailsFormArray = new FormArray<FormGroup<ICtaForm>>([]);

  private readonly campaignDatesService = inject(CampaignDatesService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly customValuesService = inject(CustomValuesService);
  private readonly sqlToAnalyticsUtilitiesService = inject(SqlToAnalyticsUtilitiesService);

  constructor() {
    effect(() => {
      this.setCampaignDates();
    });

    effect(() => {
      if (!this.isNonExecutedExecution()) {
        return;
      }

      const campaignClone = this.getCampaignClone();
      const ctaTemplate = campaignClone?.ctaTemplates?.find(
        (ct) => ct.id === this.analyticsNode().plannedExecution?.ctaId,
      );

      if (!campaignClone || !ctaTemplate) {
        return;
      }

      const ctaWithCreatorCustomValues = this.customValuesService.getCtaWithCreatorCustomValues(
        ctaTemplate,
        this.creator().id,
      );

      campaignClone.ctaTemplates = [ctaWithCreatorCustomValues];
      ctaWithCreatorCustomValues.executions = ctaWithCreatorCustomValues.executions?.filter(
        (executionTemplate) => executionTemplate.id === this.analyticsNode().plannedExecution?.ctaExecutionId,
      );

      const campaignCreator = this.campaignCreatorsFormArray.controls.find(
        (control) => control.get('creator')?.value?.id === this.creator().id,
      );

      if (!campaignCreator) {
        return;
      }

      this.ctaDetailsFormArray = campaignCreator.controls.ctas;
    });
  }

  private getCampaignClone(): TCampaignApi | null {
    if (this.campaign() && !this.isCampaignInProgress() && !this.analyticsNode().isReal) {
      return structuredClone(this.campaign());
    }

    return this.createCampaignClone();
  }

  private createCampaignClone(): TCampaignApi | null {
    const ctaTemplate = this.sqlToAnalyticsUtilitiesService.mapCtaResultToSql(
      this.creator(),
      this.ctaResult(),
      this.analyticsNode(),
    );

    if (!ctaTemplate) {
      return null;
    }

    return {
      budget: 0,
      campaignCreators: [
        {
          campaignId: this.analyticsNode().campaignId,
          creator: this.creator(),
          creatorId: this.creator().id,
          id: uuidv4(),
        },
      ],
      creatorCampaignExportId: null,
      creatorCampaignExports: [],
      ctaTemplates: [ctaTemplate],
      customer: null,
      end: this.campaign()?.end || this.campaign()?.originalEnd || '',
      iconId: null,
      id: this.analyticsNode().campaignId,
      name: '',
      notes: null,
      start: this.campaign()?.start || this.campaign()?.originalStart || '',
      status: ECampaignStatus.planned,
    };
  }

  private setCampaignDates(): void {
    const campaign = this.campaign();
    const start = campaign?.originalStart || campaign?.start;
    const end = campaign?.originalEnd || campaign?.end;

    if (!this.isNonExecutedExecution() || !start || typeof start !== 'string' || !end || typeof end !== 'string') {
      return;
    }

    this.campaignDatesService.setStartDate(this.dateUtils.parseDateTimeFromApi(start));
    this.campaignDatesService.setEndDate(this.dateUtils.parseDateTimeFromApi(end));
  }
}
